import { Box, Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useCallback, useState } from "react";
import { useRecoilState } from "recoil";

import theme from "theme/themeThree";
import { UserBadge } from "../UserBadge";
import { analytics } from "MVVM/Analytics";
import { CreatorCreditIcon } from "MVVM/Views/Pricing/Components/Table/CreatorCreditIcon";
import { AdRemovalCTAViewModel } from "./ViewModel";
import { useUser } from "MVVM/Hooks/useUser";
import { AllstarModalState } from "MVVM/State/modals";
import { ModalType } from "MVVM/@types";
import { usePortal } from "MVVM/Hooks/usePortal";
import { ProForADayConfirmation } from "../Modals/ProForADayConfirmation";
import { ProForADayButton } from "./Components/ProForADayButton";

interface AdRemovalCTAProps {
  onUpgrade?: () => void;
  onProForADay?: () => void;
  onDismiss?: () => void;
  isVisible?: boolean;
}

const AdRemovalCTA = ({
  onUpgrade,

  isVisible = true,
}: AdRemovalCTAProps) => {
  if (!isVisible) return null;

  return (
    <CTAContainer id="ad-removal-cta">
      <ContentWrapper>
        <LeftSection>
          <UserBadge userGroup="PRO" size="lg" />
          <TextContent>
            <Text
              color="envy"
              variant="text.labelMedium"
              fontWeight={600}
              mb={1}
            >
              Sick of ads?
            </Text>
            <Text color="chalk" variant="text.labelSmall" fontWeight={400}>
              Support Allstar and Subscribe to Pro or Platinum to disable ads!
            </Text>
          </TextContent>
        </LeftSection>

        <ButtonSection>
          <ProForADayButton location="remove_ads_banner" />
          <UpgradeButton
            onClick={() => {
              analytics.track("Button Click", {
                locationIdentifier: "upgrade_cta",
                location: "ad_removal_cta",
              });
              onUpgrade?.();
            }}
          >
            <Flex alignItems="center" justifyContent="center">
              <Text variant="text.labelSmall" fontWeight={600}>
                Subscribe
              </Text>
            </Flex>
          </UpgradeButton>
        </ButtonSection>
      </ContentWrapper>
    </CTAContainer>
  );
};

export { AdRemovalCTA };

const CTAContainer = styled(Flex)`
  align-items: center;
  background: linear-gradient(
    135deg,
    ${theme.colors.darkerNed} 0%,
    ${theme.colors.nedLight} 100%
  );
  border-radius: 12px;
  border: 1px solid ${theme.colors.envyAlpha80};
  justify-content: start;
  padding: 16px 20px;
  width: 100%;
  margin: 0 auto;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  @media (max-width: ${(props) => props.theme.breaks.theater}) {
    border-radius: 0;
    border-left: none;
    border-right: none;
    padding: 12px 16px;
    margin: 0;
  }
`;

const ContentWrapper = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    gap: 8px;
  }
`;

const LeftSection = styled(Flex)`
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;

  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    width: 100%;
  }
`;

const TextContent = styled(Box)`
  flex: 1;
  min-width: 0;

  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    width: 100%;
  }
`;

const ButtonSection = styled(Flex)`
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
`;

const UpgradeButton = styled(Box)`
  background: ${theme.colors.envy};
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${theme.colors.ice};
  flex-shrink: 0;
  white-space: nowrap;
  text-align: center;

  &:hover {
    background: ${theme.colors.envy};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    padding: 6px 12px;
  }
`;
